import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Check, Download, Crown, Car, UtensilsCrossed, Award, Medal, Trophy, Flag, Wine, ChevronRight, FileText } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sponsorTiers = [
  {
    id: "title",
    name: "Title Sponsor",
    price: 6000,
    spots: 1,
    icon: Crown,
    color: "#C5A059",
    featured: true,
    benefits: [
      "VIP Parking for up to 4 vehicles",
      "Four foursomes in the tournament",
      "6 tickets to the 19th Hole Happy Hour",
      "Exclusive top-tier placement on all marketing materials",
      "Opportunity to welcome the field & speak at lunch/Happy Hour",
      "Featured in exclusive BISN highlight social media reel + interview",
      "Logo on all social media posts - premium placement",
      "Logo on running slideshow in clubhouse",
      "Ad in BISN e-newsletter",
      "Exclusive opportunity to present winning team trophy",
      "Special activation opportunity (Wheel of Fortune / Deal or No Deal)"
    ]
  },
  {
    id: "cart",
    name: "Cart Sponsor",
    price: 3000,
    spots: 2,
    icon: Car,
    color: "#3A5F40",
    benefits: [
      "VIP Parking for up to 4 vehicles",
      "Three foursomes in the tournament",
      "4 tickets to the 19th Hole Happy Hour",
      "Logo displayed exclusively on all golf carts",
      "Placement on all marketing materials & signage",
      "Featured in BISN highlight social media reel",
      "Logo on social media posts - premium placement",
      "Logo on running slideshow in clubhouse",
      "Ad in BISN e-newsletter"
    ]
  },
  {
    id: "meal",
    name: "Meal Sponsor",
    price: 3000,
    spots: 2,
    icon: UtensilsCrossed,
    color: "#3A5F40",
    benefits: [
      "VIP Parking for up to 4 vehicles",
      "Three foursomes in the tournament",
      "4 tickets to the 19th Hole Happy Hour",
      "Logo displayed exclusively at all meal locations",
      "Placement on all marketing materials & signage",
      "Featured in BISN highlight social media reel",
      "Logo on social media posts - premium placement",
      "Logo on running slideshow in clubhouse",
      "Ad in BISN e-newsletter"
    ]
  },
  {
    id: "gold",
    name: "Gold Sponsor",
    price: 2000,
    spots: 6,
    icon: Trophy,
    color: "#C5A059",
    benefits: [
      "One foursome in the tournament",
      "4 tickets to the 19th Hole Happy Hour",
      "Placement on all marketing materials & signage",
      "Featured on BISN social media pages",
      "Logo on social media posts - premium placement",
      "Logo on running slideshow in clubhouse",
      "Ad in BISN e-newsletter"
    ]
  },
  {
    id: "silver",
    name: "Silver Sponsor",
    price: 1500,
    spots: 6,
    icon: Medal,
    color: "#8B9094",
    benefits: [
      "One foursome in the tournament",
      "4 tickets to the 19th Hole Happy Hour",
      "Placement on all marketing materials & signage",
      "Featured on BISN social media pages",
      "Logo on social media posts",
      "Logo on running slideshow in clubhouse"
    ]
  },
  {
    id: "bronze",
    name: "Bronze Sponsor",
    price: 1000,
    spots: 6,
    icon: Award,
    color: "#CD7F32",
    benefits: [
      "One foursome in the tournament",
      "4 tickets to the 19th Hole Happy Hour",
      "Placement on all marketing materials & signage",
      "Logo on running slideshow in clubhouse"
    ]
  },
  {
    id: "hole",
    name: "Hole Sponsorship",
    price: 500,
    spots: 10,
    icon: Flag,
    color: "#3A5F40",
    benefits: [
      "Two golfers in the tournament",
      "Company name on all marketing materials & signage",
      "Bring up to 4 representatives to your sponsored hole",
      "Branded materials or activity at your hole",
      "May bring your own tent for on-hole activation",
      "Company signage on golf cart GPS screens"
    ]
  },
  {
    id: "happyhour",
    name: "Happy Hour Sponsor",
    price: 1000,
    spots: 2,
    icon: Wine,
    color: "#8B2252",
    benefits: [
      "One foursome in the tournament",
      "Company logo on all marketing materials",
      "Recognition signage at awards ceremony",
      "Company representative at tournament & Happy Hour"
    ]
  }
];

export default function SponsorshipPage() {
  const [selectedTier, setSelectedTier] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    message: "",
    golfer_names: ["", "", "", ""],
    no_golfers: false,
    payment_method: []
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.company_name || !form.contact_name || !form.email || !form.phone || !selectedTier) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      // Filter out empty golfer names
      const filteredGolferNames = form.golfer_names.filter(name => name.trim() !== "");
      
      await axios.post(`${API}/sponsor-inquiry`, {
        company_name: form.company_name,
        contact_name: form.contact_name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        tier_interest: selectedTier,
        golfer_names: filteredGolferNames.length > 0 ? filteredGolferNames : null,
        no_golfers: form.no_golfers,
        payment_method: form.payment_method.length > 0 ? form.payment_method : null
      });
      setSubmitted(true);
      toast.success("Your form has been successfully submitted and you will be contacted within 24 hours.", {
        duration: 5000
      });
    } catch (err) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSponsorshipPDF = () => {
    const pdfUrl = "https://customer-assets.emergentagent.com/job_9d54988f-f3b8-4fd6-bed5-62f15182622d/artifacts/1rf13e93_2026%20BISN%20Golf%20Classic%20Sponsorship%20Proposal%20%281%29.pdf";
    window.open(pdfUrl, "_blank");
    toast.success("Opening sponsorship proposal...");
  };

  const handleDownloadRegistrationPDF = () => {
    const pdfUrl = "https://customer-assets.emergentagent.com/job_9d54988f-f3b8-4fd6-bed5-62f15182622d/artifacts/90las0xh_BISN_Registration_Form_Fillable_FINAL-3.pdf";
    window.open(pdfUrl, "_blank");
    toast.success("Opening registration form...");
  };

  const handlePaymentMethodChange = (method) => {
    setForm(prev => ({
      ...prev,
      payment_method: prev.payment_method.includes(method)
        ? prev.payment_method.filter(m => m !== method)
        : [...prev.payment_method, method]
    }));
  };

  const handleGolferNameChange = (index, value) => {
    setForm(prev => {
      const newGolferNames = [...prev.golfer_names];
      newGolferNames[index] = value;
      return { ...prev, golfer_names: newGolferNames };
    });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F9F8F4] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 rounded-full bg-[#3A5F40] flex items-center justify-center mx-auto mb-6">
            <Check size={40} className="text-white" />
          </div>
          <h1 className="text-4xl mb-4 text-[#1C1C1C]" data-testid="sponsor-success-title">
            Thank You!
          </h1>
          <p className="text-lg text-[#666055] mb-8">
            Your form has been successfully submitted and you will be contacted within 24 hours.
          </p>
          <Button
            onClick={() => {
              setSubmitted(false);
              setShowForm(false);
              setSelectedTier("");
              setForm({
                company_name: "",
                contact_name: "",
                email: "",
                phone: "",
                message: "",
                golfer_names: ["", "", "", ""],
                no_golfers: false,
                payment_method: []
              });
            }}
            className="btn-secondary"
            data-testid="sponsor-another-btn"
          >
            Submit Another Inquiry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F4] py-12 md:py-20">
      <div className="container-main">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-widest text-[#666055] mb-3">
            Partnership Opportunities
          </p>
          <h1 className="text-4xl md:text-5xl tracking-tight text-[#1C1C1C] mb-6" data-testid="sponsor-title">
            Become a Founding Sponsor
          </h1>
          <p className="text-lg text-[#666055] leading-relaxed mb-8">
            As an inaugural event, this is your chance to be recognized as a <strong>Founding Partner</strong> of 
            the BISN Charity Golf Classic. Your sponsorship directly supports the Sickle Cell Association of Ontario 
            while connecting your brand with 144 vetted business professionals, executives, and community leaders.
          </p>
          
          {/* Download PDF Buttons - Side by Side */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleDownloadSponsorshipPDF}
              className="bg-[#C5A059] hover:bg-[#B8934D] text-white rounded-full px-6 py-4 text-base font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 inline-flex items-center gap-2"
              data-testid="download-sponsorship-pdf"
            >
              <Download size={20} />
              2026 BISN Golf Classic Sponsorship Proposal
            </Button>
            <Button
              onClick={handleDownloadRegistrationPDF}
              className="bg-[#3A5F40] hover:bg-[#2A4430] text-white rounded-full px-6 py-4 text-base font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 inline-flex items-center gap-2"
              data-testid="download-registration-pdf"
            >
              <FileText size={20} />
              2026 BISN Registration Form
            </Button>
          </div>
        </div>

        {/* Featured Tier - Title Sponsor */}
        <div className="max-w-4xl mx-auto mb-12">
          <div 
            className={`bg-gradient-to-br from-[#2A4430] to-[#3A5F40] rounded-3xl p-8 md:p-10 text-white cursor-pointer transition-all duration-300 ${
              selectedTier === "title" ? "ring-4 ring-[#C5A059]" : ""
            }`}
            onClick={() => setSelectedTier("title")}
            data-testid="sponsor-tier-title"
          >
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 rounded-full bg-[#C5A059] flex items-center justify-center">
                    <Crown size={28} className="text-white" />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-wider text-[#C5A059] font-medium">Exclusive • 1 Spot Available</span>
                    <h2 className="text-3xl font-light">Title Sponsor</h2>
                  </div>
                </div>
                <p className="text-4xl md:text-5xl font-medium text-[#C5A059] mb-6">$6,000</p>
                <div className="grid md:grid-cols-2 gap-2">
                  {sponsorTiers[0].benefits.slice(0, 8).map((benefit, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-white/90">
                      <Check size={14} className="text-[#C5A059] mt-0.5 flex-shrink-0" />
                      {benefit}
                    </div>
                  ))}
                </div>
                {sponsorTiers[0].benefits.length > 8 && (
                  <p className="text-sm text-white/60 mt-4">+ {sponsorTiers[0].benefits.length - 8} more benefits</p>
                )}
              </div>
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all ${
                selectedTier === "title" ? "border-[#C5A059] bg-[#C5A059]" : "border-white/50"
              }`}>
                {selectedTier === "title" && <Check size={18} className="text-white" />}
              </div>
            </div>
          </div>
        </div>

        {/* Other Sponsor Tiers */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-12">
          {sponsorTiers.slice(1).map((tier) => (
            <div
              key={tier.id}
              className={`bg-white rounded-2xl border p-6 cursor-pointer transition-all duration-300 hover:shadow-[0_12px_40px_rgba(0,0,0,0.06)] ${
                selectedTier === tier.id
                  ? "border-[#3A5F40] ring-2 ring-[#3A5F40]"
                  : "border-[#E6E2D6]"
              }`}
              onClick={() => setSelectedTier(tier.id)}
              data-testid={`sponsor-tier-${tier.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${tier.color}15` }}
                  >
                    <tier.icon size={22} style={{ color: tier.color }} />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-[#1C1C1C]">{tier.name}</h3>
                    <p className="text-xs text-[#666055]">{tier.spots} spot{tier.spots > 1 ? 's' : ''} available</p>
                  </div>
                </div>
                <div
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                    selectedTier === tier.id
                      ? "border-[#3A5F40] bg-[#3A5F40]"
                      : "border-[#E6E2D6]"
                  }`}
                >
                  {selectedTier === tier.id && (
                    <Check size={14} className="text-white" />
                  )}
                </div>
              </div>

              <p className="text-3xl font-medium mb-4" style={{ color: tier.color }}>
                ${tier.price.toLocaleString()}
              </p>

              <ul className="space-y-2">
                {tier.benefits.slice(0, 4).map((benefit, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#666055]">
                    <Check size={12} className="text-[#3A5F40] mt-1 flex-shrink-0" />
                    {benefit}
                  </li>
                ))}
                {tier.benefits.length > 4 && (
                  <li className="text-xs text-[#666055]/70 pl-5">
                    + {tier.benefits.length - 4} more benefits
                  </li>
                )}
              </ul>
            </div>
          ))}
        </div>

        {/* Download Section */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="bg-white rounded-2xl border border-[#E6E2D6] p-8">
            <h3 className="text-2xl mb-3 text-[#1C1C1C]">Want All the Details?</h3>
            <p className="text-[#666055] mb-6">
              Download our complete sponsorship package with full benefit breakdowns, 
              activation ideas, and registration form.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={handleDownloadSponsorshipPDF}
                className="bg-[#C5A059] hover:bg-[#B8934D] text-white rounded-full px-6 py-3 font-medium transition-all duration-300 inline-flex items-center gap-2"
                data-testid="download-sponsor-sheet-bottom"
              >
                <Download size={18} />
                Sponsorship Proposal
              </Button>
              <Button
                onClick={handleDownloadRegistrationPDF}
                className="bg-[#3A5F40] hover:bg-[#2A4430] text-white rounded-full px-6 py-3 font-medium transition-all duration-300 inline-flex items-center gap-2"
                data-testid="download-registration-sheet-bottom"
              >
                <FileText size={18} />
                Registration Form
              </Button>
            </div>
          </div>
        </div>

        {/* CTA or Form */}
        {!showForm ? (
          <div className="text-center">
            <Button
              onClick={() => setShowForm(true)}
              disabled={!selectedTier}
              className="bg-[#3A5F40] hover:bg-[#2A4430] text-white rounded-full px-10 py-5 text-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
              data-testid="start-inquiry-btn"
            >
              {selectedTier
                ? `Inquire About ${sponsorTiers.find((t) => t.id === selectedTier)?.name}`
                : "Select a Tier to Continue"}
              <ChevronRight size={20} className="ml-2" />
            </Button>
          </div>
        ) : (
          <div className="max-w-xl mx-auto">
            <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6 md:p-8">
              <h2 className="text-2xl mb-2 text-[#1C1C1C]">Sponsorship Inquiry</h2>
              <p className="text-sm text-[#666055] mb-6">
                Interested in: <span className="font-medium text-[#3A5F40]">
                  {sponsorTiers.find((t) => t.id === selectedTier)?.name} - 
                  ${sponsorTiers.find((t) => t.id === selectedTier)?.price.toLocaleString()}
                </span>
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-[#666055]">Company Name *</Label>
                  <Input
                    value={form.company_name}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    placeholder="Your Company"
                    className="input-underline"
                    data-testid="sponsor-company-name"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#666055]">Contact Name *</Label>
                  <Input
                    value={form.contact_name}
                    onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                    placeholder="Jane Doe"
                    className="input-underline"
                    data-testid="sponsor-contact-name"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#666055]">Email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="jane@company.com"
                    className="input-underline"
                    data-testid="sponsor-email"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#666055]">Phone *</Label>
                  <Input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="(416) 555-0123"
                    className="input-underline"
                    data-testid="sponsor-phone"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#666055]">Additional Message</Label>
                  <Textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Any specific questions or customization requests?"
                    className="min-h-[100px] bg-[#F9F8F4] border border-[#E6E2D6] rounded-xl"
                    data-testid="sponsor-message"
                  />
                </div>

                {/* Golfer Names Section */}
                <div className="border-t border-[#E6E2D6] pt-6 mt-6">
                  <h3 className="text-lg font-medium text-[#1C1C1C] mb-4">Golfer Information</h3>
                  
                  {/* No Golfers Checkbox */}
                  <div className="flex items-center space-x-2 mb-4">
                    <Checkbox
                      id="no-golfers"
                      checked={form.no_golfers}
                      onCheckedChange={(checked) => setForm({ ...form, no_golfers: checked })}
                      data-testid="sponsor-no-golfers"
                    />
                    <Label 
                      htmlFor="no-golfers" 
                      className="text-sm text-[#666055] cursor-pointer"
                    >
                      Sponsor will not have any golfers attending
                    </Label>
                  </div>

                  {/* Golfer Name Inputs */}
                  {!form.no_golfers && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {[1, 2, 3, 4].map((num) => (
                        <div key={num}>
                          <Label className="text-sm font-medium text-[#666055]">
                            Golfer {num} Name <span className="text-[#999]">(Optional)</span>
                          </Label>
                          <Input
                            value={form.golfer_names[num - 1]}
                            onChange={(e) => handleGolferNameChange(num - 1, e.target.value)}
                            placeholder={`Golfer ${num}`}
                            className="input-underline"
                            data-testid={`sponsor-golfer-${num}`}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Payment Method Section */}
                <div className="border-t border-[#E6E2D6] pt-6 mt-6">
                  <h3 className="text-lg font-medium text-[#1C1C1C] mb-4">Preferred Payment Method</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {["Invoice", "Check", "eTransfer"].map((method) => (
                      <div key={method} className="flex items-center space-x-2">
                        <Checkbox
                          id={`payment-${method.toLowerCase().replace(' ', '-')}`}
                          checked={form.payment_method.includes(method)}
                          onCheckedChange={() => handlePaymentMethodChange(method)}
                          data-testid={`sponsor-payment-${method.toLowerCase().replace(' ', '-')}`}
                        />
                        <Label 
                          htmlFor={`payment-${method.toLowerCase().replace(' ', '-')}`}
                          className="text-sm text-[#666055] cursor-pointer"
                        >
                          {method}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-4 pt-4 mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowForm(false)}
                    className="flex-1 rounded-full"
                    data-testid="sponsor-back-btn"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-[#3A5F40] hover:bg-[#2A4430] text-white rounded-full"
                    data-testid="sponsor-submit-btn"
                  >
                    {loading ? "Submitting..." : "Submit Inquiry"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
